import { useEffect, useState } from 'react';
import { getStorageEstimate, listOfflineGisEntries, listOfflineMapEntries } from '../map';

/**
 * 参加地区ホームで表示する「オフライン地図の保有状況」を集約する hook
 * (Phase H、old2 `DistrictHomePage.tsx` L60-71 逐語移植)。
 *
 * old2 では `listOfflineMaps()` + `getStorageEstimate()` を inline で呼び、
 *   - 保有地区 ID 集合 (row badge 用)
 *   - 合計バイト数 (合計容量行用)
 *   - storage estimate (端末空き表示用)
 * を state に持っていた。v2 では map feature の `listOfflineMapEntries()` /
 * `getStorageEstimate()` (戻り値 field 名が `sizeBytes` / `usageBytes` / `quotaBytes`) を使う。
 *
 * `reloadTick` を渡すと再読込する (引き継ぎ実行後の一覧更新と歩調を合わせる)。
 * IndexedDB / Storage 未対応環境では空集合 / null を返す (silent)。
 */
export interface OfflineMapSummary {
  /** オフライン地図 (PMTiles) または GIS データを保有している地区 ID の集合 (row badge 用)。 */
  districtIds: Set<string>;
  /** 保有地図 (PMTiles) + GIS データの合計バイト数 (合計容量行用)。 */
  totalBytes: number;
  /** navigator.storage.estimate() の結果 (端末空き表示用、未対応は null)。 */
  storageEstimate: { usageBytes: number; quotaBytes: number } | null;
}

export function useOfflineMapSummary(reloadTick: number = 0): OfflineMapSummary {
  const [districtIds, setDistrictIds] = useState<Set<string>>(new Set());
  const [totalBytes, setTotalBytes] = useState(0);
  const [storageEstimate, setStorageEstimate] = useState<{
    usageBytes: number;
    quotaBytes: number;
  } | null>(null);

  useEffect(() => {
    let cancelled = false;
    // PMTiles (ベース地図) と GIS (重ね地図) の両 cache を集計。badge は片方でも保有していれば付く、
    // 合計容量は両者の和 (docs/98 R-50)。GIS 一覧取得は未対応環境でも PMTiles 集計を妨げない。
    void Promise.all([
      listOfflineMapEntries().catch(() => []),
      listOfflineGisEntries().catch(() => []),
    ])
      .then(([mapEntries, gisEntries]) => {
        if (cancelled) return;
        const ids = new Set<string>();
        mapEntries.forEach((e) => ids.add(e.districtId));
        gisEntries.forEach((e) => ids.add(e.districtId));
        setDistrictIds(ids);
        const mapBytes = mapEntries.reduce((sum, e) => sum + e.entry.sizeBytes, 0);
        const gisBytes = gisEntries.reduce((sum, e) => sum + e.entry.totalBytes, 0);
        setTotalBytes(mapBytes + gisBytes);
      })
      .catch(() => {
        /* IndexedDB 不可は silent */
      });
    void getStorageEstimate()
      .then((est) => {
        if (!cancelled) setStorageEstimate(est);
      })
      .catch(() => {
        /* 未対応は silent */
      });
    return () => {
      cancelled = true;
    };
  }, [reloadTick]);

  return { districtIds, totalBytes, storageEstimate };
}
